import React, { useEffect, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import htfcBadge from '@/assests/htfc-badge-2-hi-res-trans.png';
import Breadcrumbs from './Breadcrumbs';
import CommandMenu from './CommandMenu';
import Footer from './Footer';
import MobileNav from './MobileNav';
import { getNavigation } from './navigation';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import { roleHomePath } from '@/routes/roleHome';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isImpersonating, impersonatedName, stopImpersonation } = useAuth();
  const { hasWyscout, currentWorkspace, isSwitchingWorkspace } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [commandOpen, setCommandOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const navigation = user ? getNavigation(user.role, hasWyscout) : [];
  const homeHref = roleHomePath(user?.role);

  useEffect(() => {
    try {
      localStorage.setItem('sidebar-collapsed', String(collapsed));
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    const openCommandMenu = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    document.addEventListener('keydown', openCommandMenu);
    return () => document.removeEventListener('keydown', openCommandMenu);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-100">
      {isImpersonating && (
        <div className="relative z-50 flex items-center justify-between bg-amber-400 px-4 py-2 text-sm font-medium text-amber-950">
          <span>Viewing as <strong>{impersonatedName}</strong></span>
          <button
            type="button"
            onClick={async () => {
              try {
                await stopImpersonation();
              } catch {
                window.alert('Could not stop impersonating - please try again.');
              }
            }}
            className="rounded-md bg-amber-950/10 px-3 py-1 font-semibold hover:bg-amber-950/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-950"
          >
            Stop impersonating
          </button>
        </div>
      )}

      <div className={`flex min-h-screen flex-col transition-[padding] duration-200 motion-reduce:transition-none motion-reduce:duration-0 ${collapsed ? 'xl:pl-20' : 'xl:pl-72'}`}>
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex min-h-16 flex-wrap items-center gap-3 px-4 py-2 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setCommandOpen(true)}
              className="order-2 ml-auto inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus-visible:ring-club-gold-500"
              aria-label="Open command menu"
            >
              <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
              <span className="hidden md:inline">Search</span>
              <kbd className="hidden rounded border border-slate-300 px-1.5 py-0.5 text-xs font-normal dark:border-slate-600 lg:inline">Ctrl K</kbd>
            </button>

            <Link to={homeHref} className="order-1 flex min-w-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500">
              <img src={htfcBadge} alt="Hednesford Town crest" className="h-10 w-10 shrink-0 object-contain" />
              <span className="hidden truncate text-sm font-bold uppercase tracking-[0.12em] text-club-navy-900 dark:text-white sm:block">
                Hednesford Data Hub
              </span>
            </Link>

            <div data-testid="mobile-header" className="order-3 xl:hidden">
              <ThemeToggle compact />
            </div>
            <div data-testid="desktop-header" className="order-3 hidden xl:block">
              <ThemeToggle />
            </div>

            <div className="order-4 flex items-center gap-2 border-l border-slate-200 pl-3 dark:border-slate-700">
              <Link
                to="/profile"
                className="hidden rounded-md px-2 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus-visible:ring-club-gold-500 sm:block"
              >
                {user?.name || 'Profile'}
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="min-h-11 rounded-md px-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white dark:focus-visible:ring-club-gold-500"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        <div className="fixed inset-y-0 left-0 z-30 hidden xl:block">
          <Sidebar
            items={navigation}
            pathname={location.pathname}
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
          />
        </div>
        <MobileNav
          items={navigation}
          pathname={location.pathname}
          moreOpen={moreOpen}
          onMoreOpenChange={setMoreOpen}
        />

        <main className="flex-1 px-4 pb-28 pt-5 sm:px-6 lg:px-8 xl:pb-8">
          <Breadcrumbs items={navigation} pathname={location.pathname} />
          {isSwitchingWorkspace && <p role="status" className="mt-5 rounded-lg bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">Switching workspace and clearing previous data...</p>}
          <div key={currentWorkspace?.id ?? 'no-workspace'} className="mt-5">
            {children}
          </div>
        </main>
        <Footer />
      </div>

      <CommandMenu items={navigation} open={commandOpen} onClose={() => setCommandOpen(false)} />
    </div>
  );
};

export default Layout;
